"use client";

import React, { useState, useEffect } from "react";
import axios from "axios";
import { MessageCircle, Linkedin, Instagram, ExternalLink } from "lucide-react";
import Image from "next/image";

const ProfileIcon = ({ src, alt, size = "w-12 h-12" }) => (
  <div className={`${size} relative rounded-full overflow-hidden border-2 border-white shadow-lg`}>
    <Image
      src={src || "/placeholder.svg"}
      alt={alt}
      fill
      className="object-cover"
      sizes="(max-width: 768px) 100vw, 200px"
    />
  </div>
);

const MentorsPage = () => {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        const response = await axios.get("/api/mentors");
        setMentors(response.data);
      } catch (error) {
        console.error("Error fetching mentors:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMentors();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FFAE00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <section className="py-1 px-4 my-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-bricolage font-bold text-[#FFAE00] text-[50px] text-center tracking-[-0.50px] max-md:text-[32px]">
            Meet the Mentors
          </h2>
          <p className="font-bricolage font-semibold text-[#0D0D0D] text-3xl text-center tracking-[-0.30px] max-md:text-xl mb-8">
            &ldquo;The best journeys are made with the right people beside you.&rdquo;
          </p>
          <div className="grid grid-cols-1 gap-12">
            {mentors.map((mentor, idx) => (
              <div key={mentor._id || idx} className="bg-gradient-to-r from-white to-[#FFAE00]/5 rounded-3xl overflow-hidden border border-gray-100 shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
                  <div className="flex flex-col items-center lg:items-start">
                    <ProfileIcon src={mentor.image} alt={mentor.name} size="w-32 h-32 mb-6" />
                    <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4 mb-2">
                      <h3 className="text-2xl font-bold text-[#0D0D0D] text-center lg:text-left">
                        {mentor.name}
                      </h3>
                      <div className="flex items-center space-x-2">
                        {mentor.linkedin && (
                          <a href={mentor.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#FFAE00]/20 rounded-full hover:bg-[#FFAE00]/40 transition-colors">
                            <Linkedin className="w-5 h-5 text-[#0D0D0D]" />
                          </a>
                        )}
                        {mentor.instagram && (
                          <a href={mentor.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#FFAE00]/20 rounded-full hover:bg-[#FFAE00]/40 transition-colors">
                            <Instagram className="w-5 h-5 text-[#0D0D0D]" />
                          </a>
                        )}
                        <button
                          className="p-2 bg-[#FFAE00] rounded-full hover:bg-[#FFAE00]/80 transition-colors"
                          onClick={() => {
                            const url = mentor.external || mentor.linkedin;
                            if (url) window.open(url, "_blank", "noopener,noreferrer");
                          }}
                        >
                          <ExternalLink className="w-5 h-5 text-[#0D0D0D]" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[#FFAE00] font-semibold mb-4 text-center lg:text-left">
                      {mentor.role}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 w-full">
                      {mentor.achievements?.map((achievement, index) => (
                        <div
                          key={index}
                          className={`px-3 py-2 rounded-lg text-center ${
                            index === 0
                              ? "bg-gradient-to-r from-[#FFAE00] to-[#FFD700] text-white shadow-md border-2 border-[#FFAE00]"
                              : "bg-[#FFAE00]/10"
                          }`}
                        >
                          <span className={`text-sm font-semibold ${index === 0 ? "text-white" : "text-[#0D0D0D]"}`}>
                            {achievement}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-xl font-bold text-[#0D0D0D] mb-3">About</h4>
                      <p className="text-gray-700 leading-relaxed">{mentor.bio}</p>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-[#0D0D0D] mb-3">What they bring to the table</h4>
                      <p className="text-gray-700 leading-relaxed">{mentor.bringsToTable}</p>
                    </div>
                    <div className="bg-[#FFAE00]/10 p-6 rounded-2xl border-l-4 border-[#FFAE00]">
                      <MessageCircle className="w-6 h-6 text-[#FFAE00] mb-3" />
                      <p className="text-gray-700 italic text-lg">{mentor.quote}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MentorsPage;